/**
 * Two-tier audio store: Supabase Storage (source of truth) + IndexedDB
 * (write-through speed cache in front).  docs/engineering/AUDIO_DB_STORAGE.md
 *
 * Storage path : course-audio/{courseId}/{slideId}/{segIdx}_{gender}_{textHash}.{fmt}
 * IndexedDB key: v3/{courseId}/{slideId}/{segIdx}/{gender}/{textHash}/{fmt}
 *
 * EVERY input audio depends on is part of the key (decisions D6 + D7):
 *   - narration text  → textHash (narrationHash, shared with the endpoint +
 *     backfill script). A segment edited on one device can never play stale
 *     from another device's mirror — the current text produces a different
 *     key, the old entry never matches, storage has the fresh file.
 *   - voice engine    → fmt (mp3|wav). An engine change (OpenAI↔Sarvam)
 *     misses every stale entry.
 *   - voice           → gender.
 * Re-generating with identical inputs produces equivalent audio, so a mirror
 * hit is always safe to play. Superseded files simply orphan in the bucket
 * (cleanup deferred).
 *
 * Pre-v3 entries (v2/ and the original unprefixed keys) are deliberately
 * abandoned — never read, never migrated.
 *
 * Writes to Storage happen only when a Clerk session exists (decision D5) —
 * anonymous /play/ learners cache locally but never upload. Writes go through
 * /api/audio/upload (service role, ownership verified server-side) because
 * Supabase Storage validates Clerk third-party tokens inconsistently — the
 * Clerk JWT has no `role` claim (gotcha #19). Reads use the bucket's public
 * URL directly, no auth involved.
 */

import { supabase } from './supabase'
import { getNarrationVoiceConfig } from './languages'
import { narrationHash } from './audioHash'

const DB_NAME  = 'learnbee_audio_v1'
const STORE    = 'segments'
const DB_VERSION = 1
const BUCKET   = 'course-audio'

let _db: IDBDatabase | null = null

function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db)
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE)
    }
    req.onsuccess = () => { _db = req.result; resolve(req.result) }
    req.onerror   = () => reject(req.error)
  })
}

export type VoiceGender = 'female' | 'male'
export type AudioFormat = 'mp3' | 'wav'

/** Expected narration audio format for a course language + gender.
 *  Derived from the narration voice catalog (Sarvam → wav, OpenAI → mp3). */
export function audioFormatFor(language: string, gender: VoiceGender = 'female'): AudioFormat {
  return getNarrationVoiceConfig(language, gender).mimeType === 'audio/wav' ? 'wav' : 'mp3'
}

/** Sniff the real format from base64 audio bytes. WAV files begin with the
 *  RIFF magic, which is "UklGR" in base64; everything our TTS providers
 *  return otherwise is MP3. */
function sniffFormat(b64: string): AudioFormat {
  return b64.startsWith('UklGR') ? 'wav' : 'mp3'
}

function idbKey(courseId: string, slideId: string, segIdx: number, gender: VoiceGender, hash: string, fmt: AudioFormat) {
  return `v3/${courseId}/${slideId}/${segIdx}/${gender}/${hash}/${fmt}`
}

/** Storage filename for a segment (matches the upload endpoint's naming). */
export function storageAudioName(segIdx: number, gender: VoiceGender, hash: string, fmt: AudioFormat): string {
  return `${segIdx}_${gender}_${hash}.${fmt}`
}

function storagePath(courseId: string, slideId: string, segIdx: number, gender: VoiceGender, hash: string, fmt: AudioFormat) {
  return `${courseId}/${slideId}/${storageAudioName(segIdx, gender, hash, fmt)}`
}

/** True when a signed-in Clerk session exists — the only case where we
 *  attempt Storage writes (D5). */
function hasClerkSession(): boolean {
  return typeof window !== 'undefined' && !!window.Clerk?.session
}

/** True when running as a Node/vitest one-off script rather than the real
 *  browser app (no `window` at all — never the case in the deployed app).
 *  supabase.ts swaps to a service-role client in this context (see its own
 *  isScriptContext guard), so Storage writes can go straight through instead
 *  of via the Clerk-gated /api/audio/upload route. */
function isScriptContext(): boolean {
  return typeof window === 'undefined'
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

/** Fresh Clerk token for the write endpoint, or null when signed out. */
async function clerkToken(): Promise<string | null> {
  try {
    return (await window.Clerk?.session?.getToken()) ?? null
  } catch {
    return null
  }
}

// ── IndexedDB primitives ─────────────────────────────────────────────────────

async function idbPut(k: string, b64: string): Promise<void> {
  const db = await openDB()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(b64, k)
    tx.oncomplete = () => resolve()
    tx.onerror    = () => reject(tx.error)
    tx.onabort    = () => reject(tx.error || new Error('transaction aborted'))
  })
}

async function idbGet(k: string): Promise<string | null> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(k)
    req.onsuccess = () => resolve((req.result as string | undefined) ?? null)
    req.onerror   = () => reject(req.error)
  })
}

async function idbDelete(keys: string[]): Promise<void> {
  const db = await openDB()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    for (const k of keys) tx.objectStore(STORE).delete(k)
    tx.oncomplete = () => resolve()
    tx.onerror    = () => reject(tx.error)
  })
}

/** Delete every key beginning with `prefix` (used to clear a segment across
 *  all text-hash variants without knowing the old hashes). */
async function idbDeletePrefix(prefix: string): Promise<void> {
  const db = await openDB()
  const keys = await new Promise<string[]>((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).getAllKeys(IDBKeyRange.bound(prefix, prefix + '￿'))
    req.onsuccess = () => resolve((req.result as string[]) ?? [])
    req.onerror   = () => reject(req.error)
  })
  if (keys.length) await idbDelete(keys)
}

// ── base64 ⇄ bytes ───────────────────────────────────────────────────────────

function bytesToB64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const CHUNK = 8192
  for (let i = 0; i < bytes.length; i += CHUNK)
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)))
  return btoa(binary)
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Save a base64 audio segment: Storage first (when signed in), then the
 *  local IndexedDB mirror. `text` is the narration text the audio was
 *  generated FROM — it keys the entry (D7). Storage failure never blocks the
 *  local save; the error is logged. */
export async function saveAudio(
  courseId: string,
  slideId: string,
  segIdx: number,
  b64: string,
  gender: VoiceGender,
  text: string,
): Promise<void> {
  const fmt = sniffFormat(b64)
  const hash = narrationHash(text)

  const path = storagePath(courseId, slideId, segIdx, gender, hash, fmt)

  if (hasClerkSession()) {
    try {
      const token = await clerkToken()
      if (token) {
        const res = await fetch('/api/audio/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ courseId, slideId, segIdx, gender, textHash: hash, b64 }),
        })
        if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`)
      }
    } catch (e) {
      console.error('[audioCache] storage upload FAILED for', path, e)
    }
  } else if (isScriptContext()) {
    // Mirrors api/audio/upload.ts's own upload call — the script's supabase
    // client is already service-role (supabase.ts), so no Clerk token or
    // ownership check is needed here; it's implicit in the script being run
    // deliberately against a specific courseId.
    try {
      const { error } = await supabase.storage.from(BUCKET).upload(path, b64ToBytes(b64), {
        contentType: fmt === 'wav' ? 'audio/wav' : 'audio/mpeg',
        upsert: true,
        cacheControl: '60',
      })
      if (error) throw error
    } catch (e) {
      console.error('[audioCache] script-mode storage upload FAILED for', path, e)
    }
    return // no IndexedDB in a Node script — the Storage write above is it
  }

  const k = idbKey(courseId, slideId, segIdx, gender, hash, fmt)
  try {
    await idbPut(k, b64)
  } catch (e) {
    console.error('[audioCache] saveAudio FAILED for', k, e)
  }
}

/** Load a single segment's audio. Two-tier: IndexedDB → Storage → null.
 *  `text` is the CURRENT narration text — a stale entry generated from older
 *  text can never match (D7). `language` selects the expected format. */
export async function loadAudio(
  courseId: string,
  slideId: string,
  segIdx: number,
  gender: VoiceGender,
  language: string,
  text: string,
): Promise<string | null> {
  const fmt = audioFormatFor(language, gender)
  const hash = narrationHash(text)
  const k = idbKey(courseId, slideId, segIdx, gender, hash, fmt)

  try {
    const local = await idbGet(k)
    if (local != null) return local
  } catch (e) {
    console.error('[audioCache] loadAudio idb read FAILED for', k, e)
  }

  // Local miss — try Storage (public bucket, plain fetch, no auth needed).
  const path = storagePath(courseId, slideId, segIdx, gender, hash, fmt)
  try {
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
    const res = await fetch(data.publicUrl)
    if (!res.ok) return null
    const b64 = bytesToB64(await res.arrayBuffer())
    // Backfill the local mirror so the next read is instant.
    try { await idbPut(k, b64) } catch { /* mirror failure is non-fatal */ }
    return b64
  } catch {
    return null
  }
}

/** Filenames stored for a slide — ONE list call (HTTP 200 even when the
 *  folder is empty, so scans produce no red 4xx console noise, unlike
 *  per-segment probes of the public URL). */
export async function getSlideAudioSet(courseId: string, slideId: string): Promise<Set<string>> {
  try {
    const { data } = await supabase.storage.from(BUCKET).list(`${courseId}/${slideId}`, { limit: 200 })
    return new Set((data ?? []).map(o => o.name))
  } catch {
    return new Set()
  }
}

/** Per-segment voiced flags for a slide, WITHOUT downloading audio.
 *  `texts` are the CURRENT narration segments (hash-keyed, D7). Checks
 *  IndexedDB per segment; anything still unknown is resolved from a single
 *  slide-folder listing. Use for "what needs generating" scans. */
export async function slideVoicedFlags(
  courseId: string,
  slideId: string,
  texts: string[],
  genders: VoiceGender[],
  language = 'en',
): Promise<boolean[]> {
  const flags = await Promise.all(texts.map(async (t, i) => {
    const g = genders[i] ?? 'female'
    try {
      return (await idbGet(idbKey(courseId, slideId, i, g, narrationHash(t), audioFormatFor(language, g)))) != null
    } catch {
      return false
    }
  }))
  if (flags.every(Boolean)) return flags
  const stored = await getSlideAudioSet(courseId, slideId)
  return texts.map((t, i) => {
    const g = genders[i] ?? 'female'
    return flags[i] || stored.has(storageAudioName(i, g, narrationHash(t), audioFormatFor(language, g)))
  })
}

/** Load all cached audio for a slide. Returns array of base64|null per
 *  segment. `texts` are the CURRENT narration segments; per-segment gender
 *  array (or single gender) selects each voice. Listing-aware: segments
 *  absent from BOTH IndexedDB and the slide's folder listing resolve to null
 *  without issuing a GET — no red 400 per unvoiced segment. */
export async function loadSlideAudio(
  courseId: string,
  slideId: string,
  texts: string[],
  genders?: VoiceGender | VoiceGender[],
  language = 'en',
): Promise<(string | null)[]> {
  const genderAt = (i: number): VoiceGender => {
    if (Array.isArray(genders)) return genders[i] ?? 'female'
    return genders ?? 'female'
  }

  // Local mirror first (no network).
  const local = await Promise.all(
    texts.map(async (t, i) => {
      const g = genderAt(i)
      try {
        return await idbGet(idbKey(courseId, slideId, i, g, narrationHash(t), audioFormatFor(language, g)))
      } catch {
        return null
      }
    })
  )
  if (local.every(b => b != null)) return local

  // One listing tells us which of the rest actually exist in storage.
  const stored = await getSlideAudioSet(courseId, slideId)
  return Promise.all(local.map(async (b64, i) => {
    if (b64 != null) return b64
    const g = genderAt(i)
    if (!stored.has(storageAudioName(i, g, narrationHash(texts[i]), audioFormatFor(language, g)))) return null
    return loadAudio(courseId, slideId, i, g, language, texts[i])
  }))
}

/** Export-grade slide audio: STORAGE-FIRST, mirror only as fallback.
 *
 *  loadSlideAudio prefers the local mirror for playback speed — with D7 the
 *  mirror can no longer be textually stale, but exports still read storage
 *  first (cache: no-cache) so the zip always bakes the source of truth, and
 *  fall back to the mirror only for segments storage doesn't have (e.g.
 *  audio whose upload failed while offline). */
export async function loadSlideAudioForExport(
  courseId: string,
  slideId: string,
  texts: string[],
  genders?: VoiceGender | VoiceGender[],
  language = 'en',
): Promise<(string | null)[]> {
  const genderAt = (i: number): VoiceGender => {
    if (Array.isArray(genders)) return genders[i] ?? 'female'
    return genders ?? 'female'
  }
  const stored = await getSlideAudioSet(courseId, slideId)
  return Promise.all(texts.map(async (t, i) => {
    const g = genderAt(i)
    const fmt = audioFormatFor(language, g)
    const hash = narrationHash(t)
    if (stored.has(storageAudioName(i, g, hash, fmt))) {
      try {
        const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath(courseId, slideId, i, g, hash, fmt))
        const res = await fetch(data.publicUrl, { cache: 'no-cache' })
        if (res.ok) {
          const b64 = bytesToB64(await res.arrayBuffer())
          try { await idbPut(idbKey(courseId, slideId, i, g, hash, fmt), b64) } catch { /* mirror refresh is best-effort */ }
          return b64
        }
      } catch { /* fall through to mirror */ }
    }
    try {
      return await idbGet(idbKey(courseId, slideId, i, g, hash, fmt))
    } catch {
      return null
    }
  }))
}

/** Delete a single segment's audio from BOTH tiers (e.g. when narration text
 *  is edited). Clears ALL text-hash variants (prefix delete locally; the
 *  endpoint prefix-matches in the bucket) plus legacy pre-v3 keys. Storage
 *  delete requires a session (owner/editor-verified server-side). */
export async function deleteAudio(
  courseId: string,
  slideId: string,
  segIdx: number,
  gender: VoiceGender = 'female',
): Promise<void> {
  try {
    await idbDeletePrefix(`v3/${courseId}/${slideId}/${segIdx}/${gender}/`)
    // Legacy keyspaces: v2 (fmt, no hash) and the original unprefixed keys.
    const legacy = [
      `v2/${courseId}/${slideId}/${segIdx}/${gender}/mp3`,
      `v2/${courseId}/${slideId}/${segIdx}/${gender}/wav`,
      `${courseId}/${slideId}/${segIdx}/${gender}`,
    ]
    if (gender === 'female') legacy.push(`${courseId}/${slideId}/${segIdx}`)
    await idbDelete(legacy)
  } catch (e) {
    console.warn('[audioCache] deleteAudio idb failed', e)
  }

  if (hasClerkSession()) {
    try {
      const token = await clerkToken()
      if (token) {
        await fetch('/api/audio/upload', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ courseId, slideId, segIdx, gender }),
        })
      }
    } catch (e) {
      console.warn('[audioCache] deleteAudio storage failed', e)
    }
  }
}
